import React, { useState, useEffect } from 'react';
import { FileText, Search, AlertCircle, Download } from 'lucide-react';
import AdminLayout from '../components/AdminLayout';
import { supabase } from '../../lib/supabase';

interface Log {
  id: string;
  type: string;
  message: string;
  metadata: any;
  created_at: string;
}

export default function AdminLogs() {
  const [logs, setLogs] = useState<Log[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchLogs();
  }, []);

  async function fetchLogs() {
    try {
      setLoading(true);
      // Aqui você implementaria a lógica real de busca de logs
      // Por enquanto, usando dados de exemplo
      const mockLogs = [
        {
          id: '1',
          type: 'info',
          message: 'Usuário fez login',
          metadata: { userId: '123', ip: '192.168.1.1' },
          created_at: new Date().toISOString()
        },
        {
          id: '2',
          type: 'error',
          message: 'Falha na sincronização',
          metadata: { service: 'backup', error: 'Timeout' },
          created_at: new Date(Date.now() - 3600000).toISOString()
        },
        {
          id: '3',
          type: 'warning',
          message: 'Tentativa de acesso não autorizado',
          metadata: { ip: '10.0.0.1', endpoint: '/admin' },
          created_at: new Date(Date.now() - 7200000).toISOString()
        }
      ];
      
      setLogs(mockLogs);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  const filteredLogs = logs.filter(log => {
    const matchesSearch = log.message.toLowerCase().includes(searchTerm.toLowerCase()) ||
                        log.type.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filter === 'all' || log.type === filter;
    return matchesSearch && matchesFilter;
  });

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'error':
        return 'bg-red-100 text-red-600';
      case 'warning':
        return 'bg-yellow-100 text-yellow-600';
      case 'info':
        return 'bg-blue-100 text-blue-600';
      default:
        return 'bg-gray-100 text-gray-600';
    }
  };

  return (
    <AdminLayout title="Logs do Sistema">
      <div className="bg-white rounded-xl shadow-sm p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-brand-teal flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Logs do Sistema
          </h2>

          <div className="flex items-center gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar logs..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-teal focus:border-brand-teal"
              />
            </div>

            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-teal focus:border-brand-teal"
            >
              <option value="all">Todos os tipos</option>
              <option value="info">Informação</option>
              <option value="warning">Alerta</option>
              <option value="error">Erro</option>
            </select>

            <button
              onClick={() => {
                // Implementar exportação de logs
                alert('Funcionalidade em desenvolvimento');
              }}
              className="bg-brand-teal text-white px-4 py-2 rounded-lg hover:bg-brand-teal/90 transition-colors flex items-center gap-2"
            >
              <Download className="h-4 w-4" />
              Exportar
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-4 p-4 bg-red-50 text-red-600 rounded-lg flex items-start">
            <AlertCircle className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="px-4 py-2 text-left text-sm font-medium text-gray-500">Tipo</th>
                <th className="px-4 py-2 text-left text-sm font-medium text-gray-500">Mensagem</th>
                <th className="px-4 py-2 text-left text-sm font-medium text-gray-500">Detalhes</th>
                <th className="px-4 py-2 text-left text-sm font-medium text-gray-500">Data/Hora</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={4} className="px-4 py-8 text-center">
                    <div className="flex justify-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-teal"></div>
                    </div>
                  </td>
                </tr>
              ) : filteredLogs.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-4 py-8 text-center text-gray-500">
                    Nenhum log encontrado
                  </td>
                </tr>
              ) : (
                filteredLogs.map((log) => (
                  <tr key={log.id} className="border-b border-gray-200">
                    <td className="px-4 py-4">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getTypeColor(log.type)}`}>
                        {log.type}
                      </span>
                    </td>
                    <td className="px-4 py-4">
                      <p className="text-gray-900">{log.message}</p>
                    </td>
                    <td className="px-4 py-4">
                      <pre className="text-xs text-gray-600 bg-gray-50 p-2 rounded">
                        {JSON.stringify(log.metadata, null, 2)}
                      </pre>
                    </td>
                    <td className="px-4 py-4 text-gray-500">
                      {new Date(log.created_at).toLocaleString('pt-BR')}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </AdminLayout>
  );
}